import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Footer } from '@/components/Footer';
import { useAuthStore } from '@/store/authStore';
import { MobileBottomNav } from '@/components/MobileBottomNav';
import { DesktopSidebar } from '@/components/DesktopSidebar';
import { useOrderStore } from '@/store/orderStore';
import { cn } from '@/lib/utils';

export function CustomerLayout() {
    const { user, isLoading: authLoading } = useAuthStore();
    const isAppMode = !!user;
    const [isStandalone, setIsStandalone] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    
    // Eagerly prefetch orders for Optimistic UI (Zero-Delay navigation to Orders tab)
    const fetchMyOrders = useOrderStore(state => state.fetchMyOrders);

    useEffect(() => {
        if (user && user.id) {
            fetchMyOrders(user.id, 1, 20); // Silently caches orders globally
        }
    }, [user, fetchMyOrders]);

    useEffect(() => {
        // Detect if the app is running as an installed PWA (standalone mode)
        const checkStandalone = () => {
            const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches 
                || ('standalone' in window.navigator && (window.navigator as unknown as { standalone?: boolean }).standalone === true);
            setIsStandalone(isStandaloneMode);
        };
        
        checkStandalone();
        
        // Listen for display mode changes
        window.matchMedia('(display-mode: standalone)').addEventListener('change', checkStandalone);
        return () => window.matchMedia('(display-mode: standalone)').removeEventListener('change', checkStandalone);
    }, []);

    // Redirect standalone PWA users to login if they are not authenticated and trying to access the home page
    useEffect(() => {
        if (!authLoading && isStandalone && !user && location.pathname === '/') {
            navigate('/login');
        }
    }, [authLoading, isStandalone, user, location.pathname, navigate]);

    // For a native "app-like" feel, we don't show the header or footer when installed
    // Only show footer on AppHome when not installed
    const showFooter = !isAppMode && !isStandalone && (location.pathname === '/' || location.pathname === '/index.html');

    const hideBottomNav = location.pathname === '/login' || location.pathname === '/register';

    return (
        <div className={cn("min-h-[100dvh] flex flex-col", isAppMode && "lg:flex-row")}>
            {isAppMode && <DesktopSidebar />}
            
            <div className="flex-1 flex flex-col min-w-0">
                <main key={location.pathname} className="flex-1 animate-fade-in">
                    <Outlet />
                </main>
                {showFooter && <Footer />}
            </div>

            {!hideBottomNav && <MobileBottomNav />}
        </div>
    );
}
